"use client";

// shadcn navigation-menu (Base UI) https://ui.shadcn.com/docs/components/base/navigation-menu

import { NavigationMenu as NavigationMenuPrimitive } from "@base-ui/react/navigation-menu";
import * as React from "react";
import { createPortal } from "react-dom";

import { IconNavArrowDown12Line } from "@/components/icons/iconoir";
import { dropdownMenuPopupClassName } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const NavigationMenu = React.forwardRef<
  HTMLDivElement,
  NavigationMenuPrimitive.Root.Props & {
    onOpenChange?: (open: boolean) => void;
  }
>(({ className, children, onOpenChange, onValueChange, ...props }, ref) => {
  const [open, setOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <NavigationMenuPrimitive.Root
      ref={ref}
      className={cn(
        "z-10 flex max-w-max flex-1 items-center justify-center",
        className
      )}
      onValueChange={(value, eventDetails) => {
        const isOpen = value != null;
        setOpen(isOpen);
        onOpenChange?.(isOpen);
        onValueChange?.(value, eventDetails);
      }}
      {...props}
    >
      {children}
      <NavigationMenuViewport />
      {mounted &&
        createPortal(
          <div
            aria-hidden
            className={cn(
              "fixed inset-x-0 top-[var(--nav-offset)] bottom-0 z-40 bg-black/30 transition-opacity duration-200 ease-out motion-reduce:transition-none",
              open
                ? "pointer-events-auto opacity-100"
                : "pointer-events-none opacity-0"
            )}
          />,
          document.body
        )}
    </NavigationMenuPrimitive.Root>
  );
});
NavigationMenu.displayName = "NavigationMenu";

const NavigationMenuList = React.forwardRef<
  HTMLUListElement,
  NavigationMenuPrimitive.List.Props
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.List
    ref={ref}
    className={cn(
      "group flex flex-1 list-none items-center justify-center gap-x-1",
      className
    )}
    {...props}
  />
));
NavigationMenuList.displayName = "NavigationMenuList";

const NavigationMenuItem = React.forwardRef<
  HTMLLIElement,
  NavigationMenuPrimitive.Item.Props
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Item
    ref={ref}
    className={cn("relative", className)}
    {...props}
  />
));
NavigationMenuItem.displayName = "NavigationMenuItem";

// Figma nav item — body/sm: Inter 14px / 400 / line-height 150% / -0.28px,
// grey-700 (#4A4D4F), darkening to grey-950 on hover or when its menu is open.
const navigationMenuTriggerStyle =
  "font-normal text-[14px] leading-[150%] tracking-[-0.28px] relative data-popup-open:text-grey-950 border-border-strong inline-flex items-center justify-center text-grey-700 transition-colors duration-200 py-1 px-3 w-max hover:bg-white hover:text-grey-950 rounded-container";

const NavigationMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  NavigationMenuPrimitive.Trigger.Props
>(({ className, children, ...props }, ref) => (
  <NavigationMenuPrimitive.Trigger
    ref={ref}
    className={cn(
      navigationMenuTriggerStyle,
      "[&>svg]:transition-transform [&[data-popup-open]>svg]:rotate-180",
      className
    )}
    {...props}
  >
    {children}
    <IconNavArrowDown12Line
      className="ml-1.5 size-3 stroke-[1.5px]"
      aria-hidden="true"
    />
  </NavigationMenuPrimitive.Trigger>
));
NavigationMenuTrigger.displayName = "NavigationMenuTrigger";

const NavigationMenuContent = React.forwardRef<
  HTMLDivElement,
  NavigationMenuPrimitive.Content.Props
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Content
    ref={ref}
    className={cn(
      "data-ending-style:data-activation-direction=left:translate-x-[50%] data-ending-style:data-activation-direction=right:translate-x-[-50%] data-starting-style:data-activation-direction=left:translate-x-[-50%] data-starting-style:data-activation-direction=right:translate-x-[50%] left-0 bg-transparent data-ending-style:opacity-0 data-starting-style:opacity-0 md:absolute",
      className
    )}
    {...props}
  />
));
NavigationMenuContent.displayName = "NavigationMenuContent";

const NavigationMenuLink = React.forwardRef<
  HTMLAnchorElement,
  NavigationMenuPrimitive.Link.Props
>((props, ref) => <NavigationMenuPrimitive.Link ref={ref} {...props} />);
NavigationMenuLink.displayName = "NavigationMenuLink";

const NavigationMenuViewport = React.forwardRef<
  HTMLDivElement,
  NavigationMenuPrimitive.Viewport.Props
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Portal>
    <NavigationMenuPrimitive.Positioner
      align="start"
      side="bottom"
      sideOffset={4}
      className="isolate z-50 max-w-[var(--available-width)]"
    >
      <NavigationMenuPrimitive.Popup
        className={cn(
          "text-popover-foreground relative h-[var(--popup-height)] w-[var(--popup-width)] overflow-visible bg-transparent shadow-none ring-0 outline-none",
          dropdownMenuPopupClassName
        )}
      >
        <NavigationMenuPrimitive.Viewport
          className={cn(
            "relative size-full overflow-visible bg-transparent [--border-width:0px]",
            className
          )}
          ref={ref}
          {...props}
        />
      </NavigationMenuPrimitive.Popup>
    </NavigationMenuPrimitive.Positioner>
  </NavigationMenuPrimitive.Portal>
));
NavigationMenuViewport.displayName = "NavigationMenuViewport";

const NavigationMenuIndicator = React.forwardRef<
  HTMLDivElement,
  NavigationMenuPrimitive.Arrow.Props
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Arrow
    ref={ref}
    className={cn(
      "top-full z-10 flex h-1.5 items-end justify-center overflow-hidden",
      className
    )}
    {...props}
  >
    <div className="bg-border relative top-[60%] size-2 rotate-45 rounded-tl-sm" />
  </NavigationMenuPrimitive.Arrow>
));
NavigationMenuIndicator.displayName = "NavigationMenuIndicator";

export {
  navigationMenuTriggerStyle,
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuContent,
  NavigationMenuTrigger,
  NavigationMenuLink,
  NavigationMenuIndicator,
  NavigationMenuViewport,
};
